import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import type { VehicleTransmission } from './useSchoolVehicles'

export interface InstructorVehicle {
  id: string
  make: string
  model: string
  year: number | null
  colour: string | null
  kmRun: number | null
  licencePlate: string | null
  vin: string
  transmission: VehicleTransmission | null
  photoPath: string | null
}

interface CreateVehicleParams {
  make: string
  model: string
  year: number | null
  colour: string | null
  kmRun: number | null
  licencePlate: string | null
  vin: string
  transmission: VehicleTransmission | null
  photoPath: string | null
}

interface AssignedVehicle {
  make: string
  model: string
  year: number | null
  colour: string | null
  licencePlate: string | null
  vin: string | null
  transmission: VehicleTransmission | null
  photoPath: string | null
}

const VEHICLE_PHOTO_BUCKET = 'vehicle-photos'

export function useInstructorVehicle(userId: string | undefined) {
  const [ownVehicle, setOwnVehicle] = useState<InstructorVehicle | null>(null)
  const [assignedVehicle, setAssignedVehicle] = useState<AssignedVehicle | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchVehicles = async () => {
    if (!userId) return
    setLoading(true)
    setError(null)

    try {
      // Check for own vehicle (instructor_vehicles)
      const { data: ownData } = await supabase
        .from('instructor_vehicles')
        .select('*')
        .eq('instructor_id', userId)
        .maybeSingle()

      if (ownData) {
        setOwnVehicle({
          id: ownData.id,
          make: ownData.make,
          model: ownData.model,
          year: ownData.year,
          colour: ownData.colour,
          kmRun: ownData.km_run,
          licencePlate: ownData.licence_plate,
          vin: ownData.vin,
          transmission: ownData.transmission,
          photoPath: ownData.photo_path,
        })
        setAssignedVehicle(null)
      } else {
        // No own vehicle, check for school-assigned vehicle
        const { data: assignedData } = await supabase
          .from('school_vehicles')
          .select('make, model, year, colour, licence_plate, vin, transmission, photo_path')
          .limit(1)
          .maybeSingle()

        setOwnVehicle(null)
        setAssignedVehicle(assignedData ? {
          make: assignedData.make,
          model: assignedData.model,
          year: assignedData.year,
          colour: assignedData.colour,
          licencePlate: assignedData.licence_plate,
          vin: assignedData.vin,
          transmission: assignedData.transmission,
          photoPath: assignedData.photo_path,
        } : null)
      }
    } catch (err) {
      setError('Could not load vehicle information.')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchVehicles()
  }, [userId])

  const createVehicle = async (params: CreateVehicleParams) => {
    setSaving(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('create_instructor_vehicle', {
        p_make: params.make,
        p_model: params.model,
        p_year: params.year ?? undefined,
        p_colour: params.colour ?? undefined,
        p_km_run: params.kmRun ?? undefined,
        p_licence_plate: params.licencePlate ?? undefined,
        p_vin: params.vin,
        p_transmission: params.transmission ?? undefined,
        p_photo_path: params.photoPath ?? undefined,
      })

      if (rpcError) {
        console.error('RPC error:', rpcError)
        return { ok: false }
      }

      const result = data as { ok: boolean; code?: string; vehicle_id?: string }
      if (result.ok) {
        await fetchVehicles()
      }
      return result
    } catch (err) {
      console.error('Create vehicle error:', err)
      return { ok: false }
    } finally {
      setSaving(false)
    }
  }

  const updateVehicle = async (vehicleId: string, params: CreateVehicleParams) => {
    setSaving(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('update_instructor_vehicle', {
        p_vehicle_id: vehicleId,
        p_make: params.make,
        p_model: params.model,
        p_year: params.year ?? undefined,
        p_colour: params.colour ?? undefined,
        p_km_run: params.kmRun ?? undefined,
        p_licence_plate: params.licencePlate ?? undefined,
        p_vin: params.vin,
        p_transmission: params.transmission ?? undefined,
        p_photo_path: params.photoPath ?? undefined,
      })

      if (rpcError) {
        console.error('RPC error:', rpcError)
        return { ok: false }
      }

      const result = data as { ok: boolean; code?: string }
      if (result.ok) {
        await fetchVehicles()
      }
      return result
    } catch (err) {
      console.error('Update vehicle error:', err)
      return { ok: false }
    } finally {
      setSaving(false)
    }
  }

  const deleteVehicle = async (vehicleId: string) => {
    setSaving(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('delete_instructor_vehicle', {
        p_vehicle_id: vehicleId,
      })

      if (rpcError) {
        console.error('RPC error:', rpcError)
        return { ok: false }
      }

      const result = data as { ok: boolean; code?: string }
      if (result.ok) {
        await fetchVehicles()
      }
      return result
    } catch (err) {
      console.error('Delete vehicle error:', err)
      return { ok: false }
    } finally {
      setSaving(false)
    }
  }

  const uploadVehiclePhoto = async (file: File) => {
    if (!userId) return { ok: false, path: null }
    setUploadingPhoto(true)
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
      const path = `${userId}/${crypto.randomUUID()}.${ext}`
      const { error: uploadError } = await supabase.storage
        .from(VEHICLE_PHOTO_BUCKET)
        .upload(path, file, { contentType: file.type, upsert: false })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        return { ok: false, path: null }
      }
      return { ok: true, path }
    } catch (err) {
      console.error('Photo upload error:', err)
      return { ok: false, path: null }
    } finally {
      setUploadingPhoto(false)
    }
  }

  const photoUrl = (path: string | null) => {
    if (!path) return null
    return supabase.storage.from(VEHICLE_PHOTO_BUCKET).getPublicUrl(path).data.publicUrl
  }

  return {
    ownVehicle,
    assignedVehicle,
    loading,
    saving,
    uploadingPhoto,
    error,
    createVehicle,
    updateVehicle,
    deleteVehicle,
    uploadVehiclePhoto,
    photoUrl,
    refresh: fetchVehicles,
  }
}
